import { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Package, Plus, ArrowRight, ShoppingBag, Hourglass, Wallet, Sparkles } from "lucide-react";

const peso = (n) =>
  `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const CATEGORY_LABEL = {
  tarpaulin: "Tarpaulin",
  business_print: "Business Print",
  stickers_labels: "Stickers & Labels",
  signage: "Signage",
};

const Tile = ({ to, label, value, sublabel, icon: Icon, accent, testid }) => {
  const inner = (
    <div className={`pav-card transition-all hover:-translate-y-0.5 hover:shadow-md ${to ? "cursor-pointer hover:border-[#06B6D4]" : ""}`}
      data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">{label}</p>
        {Icon && (
          <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent || "bg-[#06B6D4]/10 text-[#06B6D4]"}`}>
            <Icon className="h-4 w-4" />
          </span>
        )}
      </div>
      <p className="font-display text-3xl font-extrabold text-[#0B1E3F]">{value}</p>
      {sublabel && <p className="text-xs text-neutral-500 mt-1">{sublabel}</p>}
      {to && (
        <p className="text-[11px] text-[#06B6D4] font-semibold mt-2 inline-flex items-center gap-1">
          View <ArrowRight className="h-3 w-3" />
        </p>
      )}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};

const CustomerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let done = 0;
    const tryFinish = () => { if (++done >= 2) setLoading(false); };
    api.get("/orders/mine")
      .then(({ data }) => setOrders(data))
      .catch(() => setOrders([]))
      .finally(tryFinish);
    api.get("/print-services?active_only=true")
      .then(({ data }) => setServices(data))
      .catch(() => setServices([]))
      .finally(tryFinish);
  }, []);

  const { totalOrders, inProgress, pendingPaymentCount, pendingPaymentAmount } = useMemo(() => {
    const inProg = orders.filter((o) => o.status === "paid").length;
    const pendingList = orders.filter((o) => o.status === "pending_payment");
    return {
      totalOrders: orders.length,
      inProgress: inProg,
      pendingPaymentCount: pendingList.length,
      pendingPaymentAmount: pendingList.reduce((a, b) => a + (b.total || 0), 0),
    };
  }, [orders]);

  const servicesByCategory = useMemo(() => {
    const grouped = {};
    services.forEach((s) => {
      const cat = s.category || "other";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(s);
    });
    return grouped;
  }, [services]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pav-fade-in" data-testid="customer-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">My account</p>
          <h1 className="font-display text-4xl font-extrabold text-[#0B1E3F] mt-2">Hi, {user?.name}</h1>
          <p className="text-neutral-600">Your orders and reprints, all in one place.</p>
        </div>
        <Link to="/order" className="btn-accent inline-flex items-center gap-2" data-testid="new-order-button">
          <Plus className="h-4 w-4" /> New tarpaulin order
        </Link>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Tile testid="tile-total-orders"
          to="/dashboard/orders"
          label="Total orders"
          value={loading ? "—" : totalOrders}
          sublabel="All orders, all statuses"
          icon={ShoppingBag} />
        <Tile testid="tile-in-progress"
          to="/dashboard/orders?status=paid"
          label="In progress"
          value={loading ? "—" : inProgress}
          sublabel="Confirmed payment"
          icon={Hourglass}
          accent="bg-amber-100 text-amber-600" />
        <Tile testid="tile-pending-payments"
          to="/dashboard/orders?status=pending_payment"
          label="Pending payments"
          value={loading ? "—" : pendingPaymentCount}
          sublabel={loading ? "" : `${peso(pendingPaymentAmount)} to be paid`}
          icon={Wallet}
          accent="bg-red-100 text-red-600" />
        <Tile testid="tile-total-spent"
          label="Total spent"
          value={loading ? "—" : peso(orders
            .filter((o) => o.status !== "cancelled" && o.status !== "pending_payment")
            .reduce((a, b) => a + (b.total || 0), 0))}
          sublabel="Excludes cancelled & unpaid"
          icon={Sparkles}
          accent="bg-[#06B6D4]/10 text-[#06B6D4]" />
      </div>

      {/* Services by category */}
      <div data-testid="dashboard-services-section">
        <div className="flex items-end justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Browse & re-order</p>
            <h2 className="font-display text-2xl font-extrabold text-[#0B1E3F]">Available services</h2>
          </div>
          <Link to="/order" className="text-xs font-semibold text-[#06B6D4] hover:underline inline-flex items-center gap-1">
            See all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        {loading ? (
          <p className="text-sm text-neutral-500">Loading services…</p>
        ) : services.length === 0 ? (
          <div className="pav-card text-center text-sm text-neutral-500 py-8" data-testid="dashboard-services-empty">
            No services are currently available. Please check back soon.
          </div>
        ) : (
          <div className="space-y-7">
            {Object.entries(servicesByCategory).map(([catKey, items]) => (
              <div key={catKey} data-testid={`category-${catKey}`}>
                <h3 className="font-display text-lg font-bold text-[#0B1E3F] mb-3 inline-flex items-center gap-2">
                  <Package className="h-4 w-4 text-[#06B6D4]" />
                  {CATEGORY_LABEL[catKey] || catKey}
                  <span className="text-xs text-neutral-400 font-normal">({items.length})</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((s) => (
                    <button
                      key={s.service_id}
                      type="button"
                      onClick={() => navigate(`/order?service=${s.slug || s.service_id}`)}
                      data-testid={`service-card-${s.slug || s.service_id}`}
                      className="text-left pav-card hover:-translate-y-0.5 hover:shadow-md hover:border-[#06B6D4] transition-all overflow-hidden p-0">
                      {s.cover_photo ? (
                        <img src={fileUrl(s.cover_photo)} alt={s.name}
                          className="block w-full h-32 object-cover" />
                      ) : (
                        <div className="h-32 bg-gradient-to-br from-[#0B1E3F] to-[#06B6D4] flex items-center justify-center">
                          <Package className="h-8 w-8 text-white/40" />
                        </div>
                      )}
                      <div className="p-4">
                        <p className="font-semibold text-[#0B1E3F] line-clamp-1">{s.name}</p>
                        {s.short_description && (
                          <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{s.short_description}</p>
                        )}
                        <p className="text-sm font-display font-extrabold text-[#06B6D4] mt-2.5">
                          ₱{(s.base_price || 0).toLocaleString()} <span className="text-[10px] uppercase font-medium text-neutral-400">{s.unit}</span>
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;
